import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { paymentsAPI } from "../api/api";
import { useAuth } from "../context/AuthContext";
import { Button, Alert, Card, PageHeader, Modal, Spinner } from "../components/UI";
import { CreditCard, CheckCircle } from "lucide-react";

function PlanCard({ plan, isCurrent, onSelect, loading }) {
  const isPopular = plan.id === "premium";
  return (
    <div className={`relative bg-white rounded-2xl border-2 overflow-hidden transition-all ${
      isCurrent ? "border-brand-500 shadow-lg" : isPopular ? "border-earth-400 shadow-md" : "border-brand-100 hover:border-brand-300"
    }`}>
      {isPopular && !isCurrent && (
        <div className="absolute top-0 left-0 right-0 bg-earth-500 text-white text-xs font-800 text-center py-1.5">
          ⭐ MOST POPULAR
        </div>
      )}
      {isCurrent && (
        <div className="absolute top-0 left-0 right-0 bg-brand-600 text-white text-xs font-800 text-center py-1.5">
          ✅ YOUR CURRENT PLAN
        </div>
      )}

      <div className={`p-5 ${(isPopular || isCurrent) ? "pt-9" : ""}`}>
        <div className="text-2xl mb-1">{plan.id === "free" ? "🌱" : plan.id === "premium" ? "⭐" : "🏢"}</div>
        <div className="font-900 text-brand-800 text-lg">{plan.name}</div>
        <div className="mt-2 mb-4">
          {plan.price === 0 ? (
            <span className="text-3xl font-900 text-brand-600">FREE</span>
          ) : (
            <div>
              <span className="text-3xl font-900 text-brand-800">KES {plan.price.toLocaleString()}</span>
              <span className="text-brand-400 font-700 text-sm">/{plan.period}</span>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2 mb-5">
          {plan.features.map((f, i) => (
            <div key={i} className="flex items-start gap-2 text-sm font-600 text-brand-700">
              <CheckCircle size={15} className="text-brand-500 flex-shrink-0 mt-0.5" />
              {f}
            </div>
          ))}
        </div>

        <div className="text-xs font-700 text-brand-400 mb-3">
          {plan.uploads_per_month === -1 ? "♾️ Unlimited analyses" : `📊 ${plan.uploads_per_month} analyses per month`}
        </div>

        <Button
          variant={isCurrent ? "ghost" : isPopular ? "earth" : "primary"}
          className="w-full"
          disabled={isCurrent}
          loading={loading}
          onClick={() => !isCurrent && onSelect(plan)}
        >
          {isCurrent ? "✅ Current Plan" : `Get ${plan.name}`}
        </Button>
      </div>
    </div>
  );
}

function PaymentModal({ open, plan, onClose, onConfirm, loading }) {
  const [card, setCard] = useState({ number: "", expiry: "", cvv: "", name: "" });
  const [method, setMethod] = useState("card");

  const formatCard = (v) => v.replace(/\D/g, "").replace(/(\d{4})/g, "$1 ").trim().slice(0, 19);
  const formatExpiry = (v) => {
    const d = v.replace(/\D/g, "");
    if (d.length >= 3) return d.slice(0, 2) + "/" + d.slice(2, 4);
    return d;
  };

  const handlePay = (e) => {
    e.preventDefault();
    onConfirm({ plan_id: plan?.id, payment_method: method, card_last4: card.number.replace(/\s/g, "").slice(-4) });
  };

  return (
    <Modal open={open} onClose={onClose} title={`Subscribe to ${plan?.name}`}>
      <div className="flex gap-2 mb-5">
        {[{ id: "card", label: "💳 Card" }, { id: "mpesa", label: "📱 M-Pesa" }].map((m) => (
          <button
            key={m.id}
            onClick={() => setMethod(m.id)}
            className={`flex-1 py-2.5 rounded-xl font-700 text-sm border-2 transition-all ${method === m.id ? "border-brand-500 bg-brand-50 text-brand-700" : "border-gray-200 text-gray-500 hover:border-brand-300"}`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {method === "card" ? (
        <form onSubmit={handlePay} className="flex flex-col gap-3">
          <div>
            <label className="text-sm font-700 text-brand-800 mb-1 block">Card Number</label>
            <input
              className="w-full px-4 py-3 rounded-xl border-2 border-brand-200 font-600 text-base focus:outline-none focus:border-brand-500"
              placeholder="1234 5678 9012 3456"
              value={card.number}
              onChange={(e) => setCard({ ...card, number: formatCard(e.target.value) })}
              maxLength={19}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-700 text-brand-800 mb-1 block">Expiry</label>
              <input
                className="w-full px-4 py-3 rounded-xl border-2 border-brand-200 font-600 text-base focus:outline-none focus:border-brand-500"
                placeholder="MM/YY"
                value={card.expiry}
                onChange={(e) => setCard({ ...card, expiry: formatExpiry(e.target.value) })}
                maxLength={5}
              />
            </div>
            <div>
              <label className="text-sm font-700 text-brand-800 mb-1 block">CVV</label>
              <input
                className="w-full px-4 py-3 rounded-xl border-2 border-brand-200 font-600 text-base focus:outline-none focus:border-brand-500"
                placeholder="123"
                value={card.cvv}
                onChange={(e) => setCard({ ...card, cvv: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                maxLength={4}
                type="password"
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-700 text-brand-800 mb-1 block">Cardholder Name</label>
            <input
              className="w-full px-4 py-3 rounded-xl border-2 border-brand-200 font-600 text-base focus:outline-none focus:border-brand-500"
              placeholder="Name on card"
              value={card.name}
              onChange={(e) => setCard({ ...card, name: e.target.value })}
            />
          </div>
          <div className="bg-brand-50 border border-brand-200 rounded-xl p-3 text-sm">
            <div className="flex justify-between font-700 text-brand-700">
              <span>Total per month:</span>
              <span className="font-900 text-brand-800">KES {plan?.price?.toLocaleString()}</span>
            </div>
          </div>
          <Button type="submit" loading={loading} size="lg" className="w-full">
            🔒 Pay KES {plan?.price?.toLocaleString()} Securely
          </Button>
          <p className="text-center text-xs text-gray-400 font-600">🔒 Secure · Cancel anytime · No hidden fees</p>
        </form>
      ) : (
        <form onSubmit={handlePay} className="flex flex-col gap-4">
          <div className="bg-brand-50 border-2 border-brand-200 rounded-xl p-4 text-center">
            <div className="text-4xl mb-2">📱</div>
            <p className="font-700 text-brand-700 text-sm">We will send an M-Pesa prompt to your registered phone number.</p>
          </div>
          <div className="bg-brand-50 border border-brand-200 rounded-xl p-3 text-sm">
            <div className="flex justify-between font-700 text-brand-700">
              <span>Amount:</span>
              <span className="font-900 text-brand-800">KES {plan?.price?.toLocaleString()}</span>
            </div>
          </div>
          <Button type="submit" loading={loading} size="lg" className="w-full">
            📲 Send M-Pesa Prompt
          </Button>
        </form>
      )}
    </Modal>
  );
}

export default function PaymentsPage() {
  const { user, updateUser } = useAuth();
  const location = useLocation();
  const showUpgrade = location.state?.upgrade;

  const [plans, setPlans] = useState([]);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [subscribing, setSubscribing] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    paymentsAPI.getPlans()
      .then((r) => setPlans(r.data.plans || []))
      .catch(() => {})
      .finally(() => setLoadingPlans(false));
  }, []);

  const handleConfirm = async (payData) => {
    setSubscribing(true);
    setErrorMsg("");
    try {
      const res = await paymentsAPI.subscribe(payData);
      updateUser({ plan: payData.plan_id, role: payData.plan_id === "free" ? "free" : "premium" });
      setSuccessMsg(`🎉 Success! You are now on the ${plans.find((p) => p.id === payData.plan_id)?.name} plan! Transaction: ${res.data.transaction_id}`);
      setSelectedPlan(null);
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || "Payment failed. Please try again.");
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <PageHeader title="💳 Subscription" subtitle="Choose the right plan for your farm" />

      {showUpgrade && (
        <Alert type="warning" className="mb-5">
          ⭐ Upgrade to Premium to unlock unlimited analyses and detailed treatment plans!
        </Alert>
      )}
      {successMsg && <Alert type="success" onDismiss={() => setSuccessMsg("")} className="mb-5">{successMsg}</Alert>}
      {errorMsg && <Alert type="error" onDismiss={() => setErrorMsg("")} className="mb-5">{errorMsg}</Alert>}

      {/* Current plan banner */}
      <Card className="p-4 mb-6 bg-gradient-to-r from-brand-50 to-earth-50 border-brand-200">
        <div className="flex items-center gap-3">
          <div className="text-3xl">{user?.plan === "premium" ? "⭐" : "🌱"}</div>
          <div>
            <div className="font-800 text-brand-800">Your Current Plan</div>
            <div className="font-900 text-brand-600 text-lg">{user?.plan === "premium" ? "Premium Farmer" : user?.plan === "enterprise" ? "Farm Enterprise" : "Free Farmer"}</div>
          </div>
          {user?.plan === "premium" && (
            <div className="ml-auto">
              <span className="bg-earth-200 text-earth-800 text-xs font-800 px-3 py-1.5 rounded-full">✅ Active</span>
            </div>
          )}
        </div>
      </Card>

      {loadingPlans ? (
        <div className="flex items-center justify-center py-12">
          <div className="flex flex-col items-center gap-3">
            <Spinner size={32} />
            <p className="text-brand-500 font-700">Loading plans...</p>
          </div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-3 gap-4">
          {plans.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              isCurrent={user?.plan === plan.id}
              onSelect={setSelectedPlan}
              loading={subscribing && selectedPlan?.id === plan.id}
            />
          ))}
        </div>
      )}

      <div className="mt-6 bg-brand-50 rounded-2xl border border-brand-200 p-4 text-sm text-brand-600 font-600">
        <div className="font-800 text-brand-800 mb-2">ℹ️ Good to Know</div>
        <ul className="space-y-1">
          <li>✅ Cancel anytime — no lock-in contracts</li>
          <li>📱 Payments via M-Pesa or Debit/Credit Card</li>
          <li>🔄 Upgrades take effect immediately</li>
          <li>📞 Questions? Call our support line: 0800 111 222</li>
        </ul>
      </div>

      <PaymentModal
        open={!!selectedPlan}
        plan={selectedPlan}
        onClose={() => setSelectedPlan(null)}
        onConfirm={handleConfirm}
        loading={subscribing}
      />
    </div>
  );
}
